import React, { useEffect, useState } from 'react';
import { api, apiErr } from '@/lib/api';
import { usePeriod } from '@/context/PeriodContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { toast } from 'sonner';
import { Plus, Trash2, Edit3 } from 'lucide-react';
import PeriodPicker from '@/components/PeriodPicker';
import { fmtMXN, fmtDateMX } from '@/lib/format';

export default function Deposits() {
  const { year, month } = usePeriod();
  const [period, setP] = useState(null);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ date: '', amount: '', source: '', is_cash_pending: false, notes: '' });
  const today = new Date().toISOString().slice(0, 10);

  const load = async () => {
    const p = await api.get(`/periods/lookup/${year}/${month}`);
    setP(p.data);
    const r = await api.get(`/deposits?period_id=${p.data.id}`);
    setItems(r.data);
  };
  useEffect(() => { load(); }, [year, month]);

  const startNew = () => { setEditing(null); setForm({ date: today, amount: '', source: '', is_cash_pending: false, notes: '' }); setOpen(true); };
  const startEdit = (d) => { setEditing(d); setForm({ ...d }); setOpen(true); };

  const save = async () => {
    if (!form.date || !form.amount) { toast.error('Completa fecha y monto'); return; }
    const payload = {
      period_id: period.id, date: form.date, amount: parseFloat(form.amount || 0),
      source: form.source || '', is_cash_pending: !!form.is_cash_pending, notes: form.notes || '',
    };
    try {
      if (editing) await api.patch(`/deposits/${editing.id}`, payload);
      else await api.post('/deposits', payload);
      toast.success('Registro guardado'); setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar?')) return;
    try { await api.delete(`/deposits/${id}`); toast.success('Eliminado'); load(); } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-5">
      <PeriodPicker/>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight">Depósitos & efectivo</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Los depósitos no se consideran ventas automáticas</p>
        </div>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button data-testid="btn-add-deposit" onClick={startNew} className="h-11 rounded-full px-5"><Plus className="h-4 w-4 mr-1"/> Nuevo</Button>
          </SheetTrigger>
          <SheetContent side="bottom" className="rounded-t-2xl max-h-[92vh] overflow-y-auto">
            <SheetHeader><SheetTitle className="font-display text-left">{editing ? 'Editar' : 'Nuevo registro'}</SheetTitle></SheetHeader>
            <div className="space-y-4 mt-4 pb-8">
              <div>
                <Label className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">Fecha</Label>
                <Input type="date" data-testid="dep-date" className="h-12 mt-1" value={form.date} onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))}/>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">Monto</Label>
                <Input type="number" inputMode="decimal" data-testid="dep-amount" className="h-12 mt-1 num" value={form.amount} onChange={(e) => setForm(f => ({ ...f, amount: e.target.value }))}/>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">Fuente / Banco</Label>
                <Input data-testid="dep-source" className="h-12 mt-1" placeholder="BBVA, Banorte…" value={form.source} onChange={(e) => setForm(f => ({ ...f, source: e.target.value }))}/>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-border p-3">
                <div>
                  <p className="text-sm font-medium">Efectivo pendiente de depositar</p>
                  <p className="text-xs text-muted-foreground">Marca si aún no llega al banco</p>
                </div>
                <Switch data-testid="dep-cash-pending" checked={form.is_cash_pending} onCheckedChange={(v) => setForm(f => ({ ...f, is_cash_pending: v }))}/>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">Notas</Label>
                <Textarea data-testid="dep-notes" className="mt-1" value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}/>
              </div>
              <Button data-testid="dep-save" onClick={save} className="w-full h-12 rounded-full">Guardar</Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {items.length === 0 ? (
        <Card className="p-8 text-center border-dashed"><p className="text-sm text-muted-foreground">Sin depósitos este mes.</p></Card>
      ) : (
        <div className="space-y-2" data-testid="deposits-list">
          {items.map(d => (
            <Card key={d.id} className="p-4 border-border rounded-lg" data-testid={`dep-item-${d.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm">{d.source || (d.is_cash_pending ? 'Efectivo pendiente' : 'Depósito')}</p>
                  <p className="text-xs text-muted-foreground">{fmtDateMX(d.date)}</p>
                  {d.is_cash_pending && <span className="inline-block mt-1 text-[10px] uppercase tracking-wider bg-amber-500/10 text-amber-700 dark:text-amber-500 rounded px-1.5 py-0.5">Efectivo pendiente</span>}
                </div>
                <div className="text-right">
                  <p className="font-display text-lg font-bold num">{fmtMXN(d.amount)}</p>
                  <div className="flex gap-1 justify-end mt-1">
                    <button data-testid={`dep-edit-${d.id}`} onClick={() => startEdit(d)} className="p-1.5 rounded hover:bg-secondary"><Edit3 className="h-3.5 w-3.5"/></button>
                    <button data-testid={`dep-delete-${d.id}`} onClick={() => remove(d.id)} className="p-1.5 rounded hover:bg-secondary text-destructive"><Trash2 className="h-3.5 w-3.5"/></button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
